import React, { useEffect, useState } from 'react';
import { useCms } from '@/context/CmsContext';
import { fetchPageContent } from '@/services/cms';
import styles from './cms.module.css';

export interface LinkEditRequest {
    page: string;
    section: string;
    field: string;
    originalHref: string;
    linkText: string;
}

const EVENT_NAME = 'cms:edit-link';

export function openLinkEditor(req: LinkEditRequest) {
    window.dispatchEvent(new CustomEvent<LinkEditRequest>(EVENT_NAME, { detail: req }));
}

function escapeRegex(s: string) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export default function LinkEditDialog() {
    const { trackChange, getPendingValue } = useCms();
    const [open, setOpen] = useState(false);
    const [req, setReq] = useState<LinkEditRequest | null>(null);
    const [newHref, setNewHref] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent<LinkEditRequest>).detail;
            setReq(detail);
            setNewHref(detail.originalHref);
            setError('');
            setOpen(true);
        };
        window.addEventListener(EVENT_NAME, handler);
        return () => window.removeEventListener(EVENT_NAME, handler);
    }, []);

    const handleApply = async () => {
        if (!req) return;
        if (newHref === req.originalHref) {
            setOpen(false);
            return;
        }
        setSaving(true);
        setError('');
        try {
            const pending = getPendingValue(req.page, req.section, req.field);
            let body = pending;
            if (body === undefined) {
                const content = await fetchPageContent(req.page, req.section);
                body = content?.[req.section]?.[req.field] || '';
            }

            const originalContent = body;
            const hrefPattern = new RegExp(`href="${escapeRegex(req.originalHref)}"`, 'g');
            if (!hrefPattern.test(originalContent)) {
                setError(`Couldn't locate the link in the content.`);
                setSaving(false);
                return;
            }
            const updated = originalContent.replace(hrefPattern, `href="${newHref}"`);

            // For tracking we need the *true* baseline (the saved value), not pending.
            const savedContent = pending !== undefined
                ? (await fetchPageContent(req.page, req.section))?.[req.section]?.[req.field] || ''
                : originalContent;

            trackChange(req.page, req.section, req.field, updated, savedContent, 'markdown');
            setOpen(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update link.');
        } finally {
            setSaving(false);
        }
    };

    if (!open || !req) return null;

    return (
        <div className={styles.modal} onClick={() => setOpen(false)}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <h3 className={styles.modalTitle}>Edit Link</h3>

                <div className={styles.modalSection}>
                    <label>Link text</label>
                    <div style={{ padding: '8px 10px', background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#cbd5e1', fontSize: 13 }}>
                        {req.linkText || <em style={{ color: '#64748b' }}>(no text)</em>}
                    </div>
                </div>

                <div className={styles.modalSection}>
                    <label>URL</label>
                    <input
                        type="url"
                        value={newHref}
                        onChange={(e) => setNewHref(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleApply()}
                        className={styles.editInput}
                        style={{ width: '100%' }}
                        placeholder="https://..."
                        autoFocus
                    />
                </div>

                {error && <div style={{ color: '#ef4444', fontSize: 13, marginTop: 8 }}>{error}</div>}

                <div className={styles.modalActions}>
                    <button onClick={handleApply} className={styles.saveButton} disabled={saving}>
                        {saving ? 'Applying...' : 'Apply'}
                    </button>
                    <button onClick={() => setOpen(false)} className={styles.cancelButton}>
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}
